import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Lock as LockIcon } from "@mui/icons-material";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import Swal from "sweetalert2";
import { API_URL, createAxiosInstance } from "../../api";
import successIcon from "../../assets/success-icon.svg";

const cardElementStyles = {
  base: {
    fontSize: "16px",
    color: "#32325d",
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    "::placeholder": { color: "#aab7c4" },
  },
  invalid: { color: "#fa755a", iconColor: "#fa755a" },
};

/**
 * Reusable Stripe payment form for upgrading from Basic to Premium. Used in
 * both the onboarding Subscription step and the dashboard My Plan tab so
 * users can pay inline once they've picked a cadence in `UpgradeToPremium`.
 *
 * @param {{
 *   cadence: 'monthly' | 'yearly',
 *   userId: string,
 *   onSuccess?: () => void,
 * }} props
 */
const UpgradePaymentForm = (props) => {
  const { cadence, userId, onSuccess } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!stripe || !elements) {
      Swal.fire({
        icon: "error",
        title: "Payment System Loading",
        text: "Please wait while the payment system initializes.",
      });
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      Swal.fire({
        icon: "error",
        title: "Missing Information",
        text: "Please enter both first and last name.",
      });
      return;
    }
    setLoading(true);
    try {
      const cardEl = elements.getElement(CardNumberElement);
      const { error, token } = await stripe.createToken(cardEl);
      if (error) {
        Swal.fire({ icon: "error", title: "Payment Failed", text: error.message });
        setLoading(false);
        return;
      }
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(`${API_URL}/api/billings/stripe`, {
        token: token.id,
        subscriptionType: cadence,
        name: `${firstName} ${lastName}`,
        userId,
      });

      // 3D Secure flow
      if (response.data?.requiresAction && response.data?.clientSecret) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          response.data.clientSecret
        );
        if (confirmError || paymentIntent?.status !== "succeeded") {
          if (response.data?.subscriptionId) {
            try {
              await axiosInstance.post(`${API_URL}/api/billings/cancel-incomplete`, {
                subscriptionId: response.data.subscriptionId,
              });
            } catch (cleanupErr) {
              console.error("Failed to clean up incomplete subscription:", cleanupErr);
            }
          }
          throw new Error(
            confirmError?.message ||
              "Payment authentication was not completed successfully"
          );
        }
      }

      if (response.data && (response.data.success || response.data.requiresAction)) {
        await Swal.fire({
          imageUrl: successIcon,
          imageHeight: "100px",
          title: "Payment Successful",
          text: "Your subscription has been activated!",
          confirmButtonColor: "#712d94",
        });
        window.dispatchEvent(new Event("paymentCompleted"));
        onSuccess && onSuccess();
      } else {
        throw new Error(response.data?.message || "Payment failed");
      }
    } catch (err) {
      console.error("Upgrade payment error:", err);
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "There was an issue processing your payment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{ border: "1px solid #e9e9ec", borderRadius: 2, mt: 2 }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 3,
          }}
        >
          <LockIcon sx={{ color: "#712d94", fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Details (Secure via Stripe)
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              variant="outlined"
              fullWidth
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <CardNumberElement options={{ style: cardElementStyles }} />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <CardExpiryElement options={{ style: cardElementStyles }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <CardCvcElement options={{ style: cardElementStyles }} />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={submit}
            loading={loading}
            disabled={!stripe || loading || !firstName || !lastName}
            sx={{
              minWidth: 220,
              height: 48,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Pay {cadence === "yearly" ? "£34.99/year" : "£3.99/month"}
          </LoadingButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UpgradePaymentForm;
